import type {
  Attention,
  AttentionChange,
  ChatAlerts,
  ConversationAlerts,
  ConversationAlertsChosen,
  ConversationKind,
  ConversationPlace,
} from "@beacon/core/chat"
import { queryOptions } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import { chatKeys } from "./client.ts"
import { describeUntil } from "./presence.ts"
import { isDirect } from "@beacon/core/chat/rules"

/*
 * The Member's attention in the web app: what alerts them, each conversation's mute or choice, and
 * Do not disturb. Chat keeps it, so every device agrees; a device that changes it tells the others
 * (an `attention` hint), which read it again. What it chooses changes no unread count: a muted
 * conversation is kept quiet in the lists, its count still there.
 */

export const attentionKey = [...chatKeys.all, "attention"] as const

/** The Member's attention, read again on every `attention` hint. */
export const attentionQuery = queryOptions({
  queryKey: attentionKey,
  queryFn: () => call<Attention>("GET", "/chat/attention"),
  staleTime: 60_000,
})

export const setAttention = (change: AttentionChange) =>
  call<Attention>("PUT", "/chat/attention", change)

export const setConversationAlerts = (conversationId: string, alerts: ConversationAlerts) =>
  call<ConversationAlertsChosen>("PUT", `/chat/conversations/${conversationId}/attention`, {
    alerts,
  })

/** What alerts the Member by default, as the settings offer it. */
export const alertsChoices: readonly {
  value: ChatAlerts
  short: string
  label: string
  detail: string
}[] = [
  {
    value: "all",
    short: "All",
    label: "All new messages",
    detail: "Every message in the channels you joined, as well as everything Mentions covers.",
  },
  {
    value: "mentions",
    short: "Mentions",
    label: "Direct messages and mentions",
    detail:
      "Direct messages, mentions of you or your Groups, threads you follow and your work notices.",
  },
  {
    value: "nothing",
    short: "Nothing",
    label: "Nothing",
    detail: "Nothing notifies you. Everything stays unread.",
  },
]

const conversationLabels: Record<Exclude<ConversationAlerts, "default">, string> = {
  all: "All new messages",
  mentions: "Mentions and threads",
  muted: "Muted",
}

const defaultLabels: Record<ChatAlerts, string> = {
  all: "all new messages",
  mentions: "mentions and threads",
  nothing: "nothing",
}

/**
 * What a conversation's alerts may be, its Member's default named in the first: a channel any of
 * them; a direct message, which alerts for each message anyway, its default or a mute.
 */
export function conversationAlertChoices(
  kind: ConversationKind,
  defaultAlerts: ChatAlerts,
): { value: ConversationAlerts; label: string }[] {
  const first = { value: "default" as const, label: `Default (${defaultLabels[defaultAlerts]})` }
  const direct = isDirect(kind)
  const rest = (direct ? (["muted"] as const) : (["all", "mentions", "muted"] as const)).map(
    (value) => ({ value, label: conversationLabels[value] }),
  )
  return [first, ...rest]
}

/** What the Member chose for one conversation, `default` while they chose nothing. */
export function conversationAlertsOf(
  attention: Attention | undefined,
  conversationId: string,
): ConversationAlerts {
  return (
    attention?.conversations.find(({ conversation }) => conversation.id === conversationId)
      ?.alerts ?? "default"
  )
}

export const isMuted = (attention: Attention | undefined, conversationId: string) =>
  conversationAlertsOf(attention, conversationId) === "muted"

/** `attention` with `alerts` chosen for `place`, at once: the latest chosen first. */
export function withConversationAlerts(
  attention: Attention,
  place: ConversationPlace,
  alerts: ConversationAlerts,
): Attention {
  const others = attention.conversations.filter(({ conversation }) => conversation.id !== place.id)
  return {
    ...attention,
    conversations: alerts === "default" ? others : [{ conversation: place, alerts }, ...others],
  }
}

/** How long a pause lasts, as the Member chooses it. */
export type PauseFor = "30m" | "1h" | "2h" | "tomorrow" | "week"

export const pauseChoices: readonly { value: PauseFor; label: string }[] = [
  { value: "30m", label: "For 30 minutes" },
  { value: "1h", label: "For 1 hour" },
  { value: "2h", label: "For 2 hours" },
  { value: "tomorrow", label: "Until tomorrow" },
  { value: "week", label: "Until next week" },
]

/** The morning a pause ends at, by the Member's own clock. */
const morning = 8

const minutes = { "30m": 30, "1h": 60, "2h": 120 } as const

/**
 * When a pause chosen at `now` ends: after its minutes, or at 08:00 tomorrow, or at 08:00 on the
 * next Monday.
 */
export function pausedUntilOf(choice: PauseFor, now: number): string {
  if (choice in minutes)
    return new Date(now + minutes[choice as keyof typeof minutes] * 60_000).toISOString()
  const day = new Date(now)
  const days = choice === "tomorrow" ? 1 : (8 - day.getDay()) % 7 || 7
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days, morning).toISOString()
}

/**
 * Whether Do not disturb holds the Member's notifications now, and until when: a pause's end, on
 * the reader's clock (`zone`), or the end of their quiet hours; none while it doesn't.
 */
export function describeDoNotDisturb(
  attention: Attention,
  now: number,
  zone: string,
): string | null {
  if (attention.pausedUntil && Date.parse(attention.pausedUntil) > now) {
    const until = describeUntil(attention.pausedUntil, now, zone)
    return `Paused ${until.charAt(0).toLowerCase()}${until.slice(1)}`
  }
  if (attention.doNotDisturb && attention.quietHours)
    return `Quiet hours until ${attention.quietHours.to}`
  return null
}
